// The MRZ generator's words, in the shape every language fills.
//
// The explanation below the form is the one the checker carries
// (./mrz-checker), so it is typed once and both tools print it; only the
// sentences that point at this page's own form differ. The English values come
// from the same constants the English page prints. The specimen holder the form
// starts with, the zone it builds and the worked example's digits are data and
// stay in the page and in $lib/seo/mrz, whatever the language.

import type { Sex } from "$lib/seo/mrz";
import { GENERATOR_FAQ, type ToolQuestion } from "$lib/seo/mrz-explainer";
import { fromLocale } from "../load";
import type { TranslatedLocale } from "../locales";
import { type ExplainerLinkCopy, MRZ_EXPLAINER_EN, type MrzExplainerCopy } from "./mrz-checker";

export interface MrzGeneratorCopy {
  readonly title: string;
  /** At most 160 characters. */
  readonly description: string;
  readonly heading: string;
  readonly intro: string;
  readonly labels: {
    readonly surname: string;
    readonly givenNames: string;
    readonly issuingState: string;
    readonly nationality: string;
    readonly documentNumber: string;
    readonly personalNumber: string;
    readonly dateOfBirth: string;
    readonly expiryDate: string;
    readonly sex: string;
  };
  /** The sex select's options, by the value the zone is built from. */
  readonly sexes: Readonly<Record<Sex, string>>;
  readonly outputHeading: string;
  readonly copy: string;
  readonly copied: string;
  /**
   * The line under the introduction that sends a reader who already has a
   * zone to the checker. The anchor is the checker's own heading in the same
   * language, so the link names the page it leads to and this page does not
   * compete with it for the checker's searches.
   */
  readonly checkerCallout: {
    readonly before: string;
    readonly anchor: string;
    readonly after: string;
  };
  /** The line under the output, as HTML with one link to `/mrz-checker`. */
  readonly verifyHtml: string;
  readonly checkDigitHeading: string;
  /**
   * The sentence after the worked example, as HTML. `field` and `digit` arrive
   * already wrapped in their `<code>` elements.
   */
  readonly workedExampleResult: (field: string, digit: string) => string;
  readonly faq: readonly ToolQuestion[];
  readonly checkerLink: ExplainerLinkCopy;
  readonly explainer: MrzExplainerCopy;
}

export const MRZ_GENERATOR_EN: MrzGeneratorCopy = {
  title: "MRZ generator – build a valid synthetic passport MRZ (TD3)",
  description:
    "Generate a valid ICAO 9303 TD3 MRZ (machine-readable zone) with correct check digits from synthetic inputs. Client-side; for testing, never real personal data.",
  heading: "MRZ generator",
  intro:
    "Build a valid passport machine-readable zone (ICAO 9303 TD3) with correct check digits. Use it " +
    "to create test fixtures – the output is synthetic and must never carry real personal data.",
  labels: {
    surname: "Surname",
    givenNames: "Given names",
    issuingState: "Issuing state (alpha-3)",
    nationality: "Nationality (alpha-3)",
    documentNumber: "Document number",
    personalNumber: "Personal number (optional)",
    dateOfBirth: "Date of birth (YYMMDD)",
    expiryDate: "Expiry date (YYMMDD)",
    sex: "Sex",
  },
  sexes: { M: "Male", F: "Female", X: "Unspecified" },
  outputHeading: "Generated MRZ",
  copy: "Copy",
  copied: "Copied",
  checkerCallout: {
    before: "Checking a zone you already have? Paste it into the",
    anchor: "MRZ parser and check digit calculator",
    after: ".",
  },
  verifyHtml: 'Verify it with the <a href="/mrz-checker">MRZ parser</a>.',
  checkDigitHeading: "How the check digits above are calculated",
  workedExampleResult: (field, digit) =>
    `Enter ${field} as the document number above and the digit after it in the generated zone ` +
    `is ${digit} – the same arithmetic, run by the same code.`,
  faq: GENERATOR_FAQ,
  checkerLink: {
    label: "MRZ parser",
    blurb: "paste a zone and watch every check digit recomputed.",
  },
  explainer: MRZ_EXPLAINER_EN,
};

const modules = import.meta.glob("../locales/*/mrz-generator.ts");

export function loadMrzGeneratorCopy(locale: TranslatedLocale): Promise<MrzGeneratorCopy> {
  return fromLocale<MrzGeneratorCopy>(modules, locale, "mrz-generator");
}
